/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import type {
  Product
} from "../types/catalog";

interface CartItem {
  product: Product;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  addItem: (
    product: Product,
    quantity?: number
  ) => void;
  removeItem: (
    productId: string
  ) => void;
  updateQuantity: (
    productId: string,
    quantity: number
  ) => void;
  clearCart: () => void;
}

const STORAGE_KEY =
  "rini_cart_v1";

const CartContext =
  createContext<
    CartContextValue | undefined
  >(undefined);

export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] =
    useState<CartItem[]>(() => {
      try {
        const raw =
          localStorage.getItem(
            STORAGE_KEY
          );

        if (!raw) {
          return [];
        }

        return JSON.parse(raw);
      } catch {
        return [];
      }
    });

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(items)
    );
  }, [items]);

  function addItem(
    product: Product,
    quantity = 1
  ) {
    if (
      product.stockQuantity <= 0
    ) {
      return;
    }

    setItems((current) => {
      const existing =
        current.find(
          (item) =>
            item.product.id ===
            product.id
        );

      if (existing) {
        return current.map(
          (item) =>
            item.product.id ===
            product.id
              ? {
                  ...item,

                  product,

                  quantity:
                    Math.min(
                      product.stockQuantity,
                      item.quantity +
                        quantity
                    )
                }
              : item
        );
      }

      return [
        ...current,
        {
          product,

          quantity:
            Math.min(
              product.stockQuantity,
              Math.max(
                1,
                quantity
              )
            )
        }
      ];
    });
  }

  function removeItem(
    productId: string
  ) {
    setItems((current) =>
      current.filter(
        (item) =>
          item.product.id !==
          productId
      )
    );
  }

  function updateQuantity(
    productId: string,
    quantity: number
  ) {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems((current) =>
      current.map(
        (item) => {
          if (
            item.product.id !==
            productId
          ) {
            return item;
          }

          const availableStock =
            Math.max(
              0,
              item.product
                .stockQuantity
            );

          if (
            availableStock <= 0
          ) {
            return {
              ...item,
              quantity: 0
            };
          }

          return {
            ...item,

            quantity:
              Math.min(
                quantity,
                availableStock
              )
          };
        }
      )
      .filter(
        (item) =>
          item.quantity > 0
      )
    );
  }

  function clearCart() {
    setItems([]);
  }

  const itemCount =
    useMemo(
      () =>
        items.reduce(
          (
            total,
            item
          ) =>
            total +
            item.quantity,
          0
        ),
      [items]
    );

  const subtotal =
    useMemo(
      () =>
        items.reduce(
          (
            total,
            item
          ) =>
            total +
            Number(
              item.product.price
            ) *
              item.quantity,
          0
        ),
      [items]
    );

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotal,
        addItem,
        removeItem,
        updateQuantity,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context =
    useContext(CartContext);

  if (!context) {
    throw new Error(
      "useCart must be used inside CartProvider"
    );
  }

  return context;
}
